/**
 * Universal print helpers.
 *
 * Two flavours:
 *   - `printHtml(html, title?)` — wraps an HTML fragment in a print-ready
 *     document, writes it into a hidden iframe, and triggers
 *     `window.print()`. The iframe is cleaned up after the print dialog
 *     closes.
 *   - `printUrl(url, title?)` — loads a remote file (image, PDF, doc URL)
 *     into a hidden iframe and prints it. Works for same-origin and CORS-
 *     friendly URLs.
 */

const A4_LANDSCAPE_CSS = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Manrope, Calibri, Arial, sans-serif; color: #1A2E2E; margin: 0; padding: 0; }
  h1 { color: #003DA5; font-size: 18px; margin: 0 0 6px 0; }
  h2 { color: #003DA5; font-size: 13px; margin: 12px 0 4px 0; }
  p { margin: 4px 0; font-size: 11px; line-height: 1.4; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 9px; margin-top: 6px; }
  th, td { border: 1px solid #C9D6E6; padding: 3px 4px; text-align: left; vertical-align: top; word-wrap: break-word; overflow-wrap: anywhere; }
  thead tr { background: #003DA5; color: white; }
  tbody tr:nth-child(even) td { background: #F4F8FD; }
  ul { padding-left: 18px; margin: 4px 0; }
  li { font-size: 10px; line-height: 1.4; }
  .kicker { font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase; color: #1F4FBE; font-weight: 800; }
  .meta { font-size: 9px; color: #4B5563; margin-bottom: 8px; }
  .pill { display: inline-block; padding: 1px 6px; border-radius: 9px; font-size: 8px; font-weight: 800; background: #EAF2FF; color: #1F4FBE; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  img { max-width: 100%; max-height: 200mm; height: auto; display: block; }
  .doc-img { width: 100%; max-height: 95vh; object-fit: contain; }
`;

function _mountIframe() {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);
  return iframe;
}

function _cleanup(iframe) {
  // Give the browser time to fully dismiss the print dialog. Some
  // browsers (Safari) fire afterprint synchronously while the dialog is
  // still up; removing the iframe too early crashes the dialog.
  setTimeout(() => {
    try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
  }, 1500);
}

export function printHtml(bodyHtml, title = 'Print') {
  const iframe = _mountIframe();
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) return;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${A4_LANDSCAPE_CSS}</style></head><body>${bodyHtml}</body></html>`);
  doc.close();
  const win = iframe.contentWindow;
  if (!win) { _cleanup(iframe); return; }
  // Wait one tick so images/fonts inside the iframe paint before print.
  const fire = () => {
    try {
      win.focus();
      win.print();
    } catch (e) { /* ignore */ }
    _cleanup(iframe);
  };
  // If the body has images, wait for them to load (best-effort, 1.2s cap).
  const imgs = doc.images ? Array.from(doc.images) : [];
  if (imgs.length === 0) {
    setTimeout(fire, 60);
    return;
  }
  let loaded = 0;
  let done = false;
  const finish = () => { if (!done) { done = true; fire(); } };
  imgs.forEach((im) => {
    if (im.complete) { loaded += 1; if (loaded === imgs.length) finish(); return; }
    im.addEventListener('load', () => { loaded += 1; if (loaded === imgs.length) finish(); });
    im.addEventListener('error', () => { loaded += 1; if (loaded === imgs.length) finish(); });
  });
  setTimeout(finish, 1200);
}

export function printUrl(url, title = 'Print') {
  if (!url) return;
  const lower = (url || '').toLowerCase();
  // For PDFs: an embedded iframe with the PDF URL prints natively in
  // Chrome / Edge / Firefox. Safari opens the PDF in a new tab on
  // window.print() — acceptable fallback. We try iframe-print first.
  if (lower.endsWith('.pdf') || lower.includes('application/pdf')) {
    const iframe = _mountIframe();
    iframe.src = url;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        // Cross-origin or browser blocked — fall back to opening in new tab.
        window.open(url, '_blank', 'noopener');
      }
      _cleanup(iframe);
    };
    // Safety net if the load never fires (CORS/blocked).
    setTimeout(() => _cleanup(iframe), 8000);
    return;
  }
  // Image (or anything that renders inside an <img>): embed in HTML.
  const safeUrl = url.replace(/"/g, '%22');
  printHtml(`<img class="doc-img" src="${safeUrl}" alt="${title}"/>`, title);
}
